import { Button, Checkbox, FormGrid, FormSection, IconButton, TextField } from '../../components/ui'
import { slugFrom, type ProjectPackageInput, type ProjectProposalInput } from '../../api/projectProposals'

/** I dati della proposta: da qui l'IA scrive la pagina. Fee e pacchetti finiscono sulla pagina così come sono. */
export function ProposalForm({ value, onChange, slugLocked }: {
  value: ProjectProposalInput
  onChange: (v: ProjectProposalInput) => void
  /** Dopo la creazione l'indirizzo non cambia: è quello della pagina pubblicata. */
  slugLocked?: boolean
}) {
  const set = <K extends keyof ProjectProposalInput>(k: K, v: ProjectProposalInput[K]) => onChange({ ...value, [k]: v })
  const setPkg = (i: number, p: Partial<ProjectPackageInput>) =>
    set('pacchetti', value.pacchetti.map((x, j) => j === i ? { ...x, ...p } : p.consigliato ? { ...x, consigliato: false } : x))
  const text = (k: keyof ProjectProposalInput) => (value[k] as string | null | undefined) ?? ''
  const money = (s: string) => { const n = Number(s.replace(',', '.')); return Number.isFinite(n) ? n : 0 }

  return (
    <div className="d-flex flex-column gap-4">
      <FormSection title="Cliente">
        <FormGrid columns={2}>
          <TextField label="Nome del cliente" required value={value.cliente} onChange={e => set('cliente', e.target.value)} />
          <TextField
            label="Indirizzo della pagina"
            prefix="progetti.equalstudio.it/"
            value={text('slug')}
            placeholder={slugFrom(value.cliente)}
            disabled={slugLocked}
            supportingText={slugLocked ? 'L’indirizzo non cambia dopo la creazione.' : 'Vuoto = ricavato dal nome.'}
            onChange={e => set('slug', e.target.value.toLowerCase())}
          />
          <TextField label="Settore" value={text('settore')} onChange={e => set('settore', e.target.value)} />
          <TextField label="Sito" value={text('sito')} onChange={e => set('sito', e.target.value)} />
          <TextField label="Social" value={text('social')} onChange={e => set('social', e.target.value)} />
          <TextField label="Tipo di documento" value={text('tipo')} onChange={e => set('tipo', e.target.value)} />
          <TextField label="Data" placeholder="Mese corrente" value={text('data')} onChange={e => set('data', e.target.value)} />
        </FormGrid>
      </FormSection>

      <FormSection title="Il progetto" description="L’IA scrive i testi da qui: più è concreto, meglio è.">
        <div className="d-flex flex-column gap-3">
          <TextField label="Situazione del cliente" multiline rows={4} value={text('situazione')} onChange={e => set('situazione', e.target.value)} />
          <TextField label="Obiettivi" multiline rows={3} value={text('obiettivi')} onChange={e => set('obiettivi', e.target.value)} />
          <TextField label="Servizi proposti" multiline rows={3} value={text('servizi')} onChange={e => set('servizi', e.target.value)} />
          <TextField label="Tempi" multiline rows={2} value={text('tempi')} onChange={e => set('tempi', e.target.value)} />
          <TextField label="Appunti" multiline rows={4} supportingText="Note della chiamata, materiale del cliente, richieste particolari."
            value={text('note')} onChange={e => set('note', e.target.value)} />
        </div>
      </FormSection>

      <FormSection title="Fee di avvio">
        <FormGrid columns={3}>
          <TextField label="Importo (€)" inputMode="decimal" value={String(value.feeImporto ?? 0)} onChange={e => set('feeImporto', money(e.target.value))} />
          <TextField label="Prezzo pieno (€)" inputMode="decimal" supportingText="Se più alto, si vede barrato."
            value={value.feeImportoPieno == null ? '' : String(value.feeImportoPieno)}
            onChange={e => set('feeImportoPieno', e.target.value.trim() === '' ? null : money(e.target.value))} />
          <TextField label="Cosa comprende" value={text('feeDescrizione')} onChange={e => set('feeDescrizione', e.target.value)} />
        </FormGrid>
      </FormSection>

      <FormSection title="Pacchetti" description="Da uno a tre. Prezzi e nomi compaiono sulla pagina così come li scrivi.">
        <div className="d-flex flex-column gap-3">
          {value.pacchetti.map((p, i) => (
            <div key={i} className="d-flex flex-column gap-2">
              <FormGrid columns={3}>
                <TextField label={`Pacchetto ${i + 1}`} value={p.nome} onChange={e => setPkg(i, { nome: e.target.value })} />
                <TextField label="Prezzo" placeholder="€490" value={p.prezzo} onChange={e => setPkg(i, { prezzo: e.target.value })} />
                <TextField label="Unità" placeholder="mese, lead qualificato…" value={p.unita ?? ''} onChange={e => setPkg(i, { unita: e.target.value })} />
              </FormGrid>
              <div className="d-flex align-items-start gap-2">
                <div className="flex-grow-1">
                  <TextField label="Cosa comprende" multiline rows={2} value={p.descrizione ?? ''} onChange={e => setPkg(i, { descrizione: e.target.value })} />
                </div>
                {value.pacchetti.length > 1 && (
                  <IconButton icon="delete" aria-label={`Togli il pacchetto ${i + 1}`}
                    onClick={() => set('pacchetti', value.pacchetti.filter((_, j) => j !== i))} />
                )}
              </div>
              <Checkbox label="Consigliato" checked={p.consigliato} onChange={e => setPkg(i, { consigliato: e.target.checked })} />
            </div>
          ))}
          {value.pacchetti.length < 3 && (
            <div>
              <Button variant="text" icon="add"
                onClick={() => set('pacchetti', [...value.pacchetti, { nome: '', prezzo: '', unita: '', descrizione: '', consigliato: false }])}>
                Aggiungi pacchetto
              </Button>
            </div>
          )}
        </div>
      </FormSection>
    </div>
  )
}
